import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, User, Shield, Briefcase, Headphones, Pencil, HardHat } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import EditTeamMemberModal from "../../components/EditTeamMemberModal"; 

// Recursive Tree Item
const TeamNodeItem = ({ node, onEdit }: { node: any, onEdit: (u: any) => void }) => {
  const [isOpen, setIsOpen] = useState(true);
  
  // Executive ke niche koi nahi hoga, baki sabke niche ho sakte hain
  const hasChildren = node.children && node.children.length > 0;

  // Icon Helper (Updated with Site Engineer Icon)
  const getIcon = (role: string) => {
    switch(role) {
      case "SUPER_ADMIN": return <Shield size={16} className="text-purple-500"/>;
      case "ADMIN": return <Shield size={16} className="text-red-500"/>;
      case "MANAGER": return <Briefcase size={16} className="text-amber-500"/>;
      case "TEAM_LEADER": return <User size={16} className="text-blue-500"/>;
      case "AGENT": return <Headphones size={16} className="text-green-500"/>;
      case "EXECUTIVE": return <HardHat size={16} className="text-orange-500"/>; // Site Engineer
      default: return <User size={16}/>;
    }
  };

  return (
    <div className="ml-6 border-l border-zinc-700 pl-4 relative font-[Serif]">
      <div className="absolute top-4 -left-4 w-4 h-[1px] bg-zinc-700"></div>

      <div className="mb-4">
        <div className={`flex items-center gap-4 bg-[#1c1c1c] border border-zinc-800 p-4 rounded-lg transition-all group hover:border-zinc-600`}>
          
          {/* Expand/Collapse */}
          <div onClick={() => hasChildren && setIsOpen(!isOpen)} className="cursor-pointer">
             {hasChildren ? (isOpen ? <ChevronDown size={16} className="text-zinc-500"/> : <ChevronRight size={16} className="text-zinc-500"/>) : <span className="w-4 h-4"></span>}
          </div>

          <div className="bg-zinc-900 p-2 rounded-full border border-zinc-800">{getIcon(node.role)}</div>

          <div className="flex-1">
             <h4 className="text-white font-bold text-sm">{node.name}</h4>
             <p className="text-zinc-500 text-xs">{node.email}</p>
          </div>

          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded mr-4 ${
              node.department === 'SITE' ? 'bg-blue-900 text-blue-200' : 'bg-zinc-800 text-zinc-300'
          }`}>
            {node.role === 'EXECUTIVE' ? 'SITE ENGINEER' : node.role.replace("_", " ")}
          </span>

          {/* EDIT BUTTON */}
          <button 
            onClick={() => onEdit(node)}
            className="opacity-0 group-hover:opacity-100 bg-zinc-800 hover:bg-amber-600 hover:text-black text-white p-2 rounded transition-all"
            title="Edit Member"
          >
            <Pencil size={14} />
          </button>
        </div>

        {/* RECURSION */}
        {hasChildren && isOpen && (
          <div className="mt-2 animate-in slide-in-from-top-2 fade-in">
            {node.children.map((child: any) => (
              <TeamNodeItem key={child.id} node={child} onEdit={onEdit} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Subordinates = () => {
  const { token } = useAdminAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [editingUser, setEditingUser] = useState<any>(null);

  const fetchTeam = async () => {
    setLoading(true);
    setError("");
    try {
      // const res = await fetch("http://localhost:1001/api/admin/team/my-subordinates", {
      const res = await fetch("https://luxuriyahomes.com/api/admin/team/my-subordinates", {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 1. Check for 403 or other errors first
      if (!res.ok) {
         if(res.status === 403) throw new Error("Access Denied: You do not have permission to view subordinates.");
         throw new Error("Failed to fetch team data");
      }

      // 2. Safely parse JSON
      const data = await res.json();
      setTree(data);

    } catch (err: any) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeam(); }, []);

  return (
    <div className="text-white min-h-screen p-4">
      <div className="mb-8">
        <h2 className="text-3xl font-light text-amber-500">Hierarchy View</h2>
        <p className="text-zinc-500 text-sm">Manage & Edit your team structure</p>
      </div>

      {loading && <p>Loading hierarchy...</p>}
      
      {error && (
          <div className="p-4 bg-red-900/20 border border-red-800 text-red-400 rounded">
              {error}
          </div>
      )}

      {!loading && !error && (
        <div className="p-4 border border-zinc-800 rounded-xl bg-black/20">
          {tree.length === 0 ? (
             <p className="text-zinc-500">No subordinates found under you.</p>
          ) : (
             tree.map((node: any) => (
                <TeamNodeItem key={node.id} node={node} onEdit={(u) => setEditingUser(u)} />
             ))
          )}
        </div>
      )}

      {/* EDIT MODAL */}
      {editingUser && (
        <EditTeamMemberModal 
            user={editingUser} 
            onClose={() => setEditingUser(null)} 
            onSuccess={() => {
                setEditingUser(null);
                fetchTeam();
            }}
        />
      )}
    </div>
  );
};

export default Subordinates;